import { useCallback, useEffect, useRef } from 'react';
import { createReconnectingSocket } from '../../../../shared/sockets';
import { CLIENT_EVENTS } from '../constants/socketEvents';
import { buildInterveneChatSocketUrl } from '../urls/buildInterveneSocketUrls';

export function useInterveneChatSocket({
  organisationId,
  botNumber,
  phoneNumber,
  agentName,
  onPayload,
  onStatusChange,
}) {
  const socketRef = useRef(null);
  const onPayloadRef = useRef(onPayload);
  onPayloadRef.current = onPayload;

  const notifyActiveUser = useCallback(() => {
    if (!botNumber || !phoneNumber || !agentName) return;
    socketRef.current?.send({
      type: CLIENT_EVENTS.ACTIVE_USER,
      bot_number: botNumber,
      phone_number: phoneNumber,
      agent_name: agentName,
    });
  }, [agentName, botNumber, phoneNumber]);

  const sendMarkRead = useCallback(() => {
    if (!botNumber || !phoneNumber) return;
    socketRef.current?.send({
      type: CLIENT_EVENTS.MARK_READ,
      bot_number: botNumber,
      phone_number: phoneNumber,
    });
  }, [botNumber, phoneNumber]);

  const sendWatchBot = useCallback(() => {
    if (!botNumber) return;
    socketRef.current?.send({
      type: CLIENT_EVENTS.WATCH_BOT,
      bot_number: botNumber,
    });
  }, [botNumber]);

  const sendWatchBotRef = useRef(sendWatchBot);
  sendWatchBotRef.current = sendWatchBot;

  const notifyActiveUserRef = useRef(notifyActiveUser);
  notifyActiveUserRef.current = notifyActiveUser;

  useEffect(() => {
    const url = buildInterveneChatSocketUrl(botNumber, organisationId);
    if (!url) return undefined;

    const socket = createReconnectingSocket(url, {
      onOpen: () => {
        sendWatchBotRef.current();
        notifyActiveUserRef.current();
      },
    });
    socketRef.current = socket;

    const unsubStatus = socket.on('status', (status) => {
      onStatusChange?.(status);
    });
    const unsubMessage = socket.on('message', (payload) => {
      onPayloadRef.current?.(payload);
    });

    return () => {
      unsubStatus();
      unsubMessage();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [botNumber, organisationId, onStatusChange]);

  useEffect(() => {
    sendWatchBot();
  }, [sendWatchBot]);

  useEffect(() => {
    notifyActiveUser();
  }, [notifyActiveUser]);

  return { notifyActiveUser, sendMarkRead };
}
